import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import React from "react";

import api, {
  GetRecordingResponse,
  Recording,
  TranscriptCaption,
} from "../../../hooks/api-new";
import { useAxios } from "../../../hooks/axios";
import Loader from "../../common/Loader";
import { TextLanguage, textLanguageNames } from "../../../types";
import ReactMarkdown from "react-markdown";
import { isAxiosError, HttpStatusCode } from "axios";
import ShareRecording from "./ShareRecording";
import useUser from "../../../hooks/user";
import { Document, ExternalHyperlink, Packer, Paragraph, TextRun } from "docx";
import Button from "../../common/Button";
import Select from "../../common/Select";
import classNames from "./Recording.module.css";
import Error from "../../common/Error";
import SimpleBar from "simplebar-react";
import Search from "../../common/Search";
import { FiDownload } from "react-icons/fi";
import Breadcrumbs from "../../common/Breadcrumbs";
import Empty from "../../common/Empty";
import { useIsMobile } from "../../../hooks/layout";

const RecordingPage = () => {
  const params = useParams();
  const recordingId = useMemo(() => params.id, [params.id]);

  const [loadingState, setLoadingState] = useState<
    | "loading"
    | "recordingInSession"
    | "error"
    | "done"
    | "unauthorized"
    | "notFound"
  >("loading");
  const [recordingLanguage, setRecordingLanguage] = useState<string>("en");
  const [availableLanguageLocales, setAvailableLanguageLocales] = useState<
    string[]
  >([]);
  const [captionTimestamps, setCaptionTimestamps] = useState<
    { timestamp: number; id: string }[] //timestamp in seconds
  >([]);

  const [highlightedCaption, setHighlightedCaption] = useState<{
    id: string;
  }>();
  const [searchedCaptions, setSearchedCaptions] = useState<
    TranscriptCaption[] | undefined
  >([]);

  const [query, setQuery] = useState<string>("");
  const mediaPlayer = useRef<HTMLVideoElement | HTMLAudioElement | null>(null);

  const axios = useAxios();
  const getRecording = api.useGetRecording(axios);
  const generateSummary = api.useGenerateSummary(axios);
  const [recording, setRecording] = useState<Recording>();
  const [mediaUrl, setMediaUrl] = useState<string>();
  const [transcript, setTranscript] = useState<TranscriptCaption[]>([]);
  const [user] = useUser();
  const [host, setHost] = useState<string>();
  const [participants, setParticipants] = useState<
    GetRecordingResponse["participants"]
  >([]);
  const [timeOffset, setTimeOffset] = useState<number>(0);
  const [captionClicked, setCaptionClicked] = useState<boolean>(false);

  // Load time offset from local storage
  useEffect(() => {
    const savedOffset = localStorage.getItem("TIME_OFFSET");
    if (savedOffset) {
      setTimeOffset(parseFloat(savedOffset));
    }
    console.log(
      "Use timeOffset in local storage to adjust caption timing\nE.g.: window.timeOffset = -2.5 will shift captions 2.5s earlier"
    );
    console.log(`Current TIME_OFFSET: ${savedOffset || 0}`);
  }, []);

  const computeCaptionTimestamp = useCallback(
    (caption: TranscriptCaption, estimatedFirstFrameTime: string) => {
      return (
        (new Date(caption.first_created).getTime() -
          new Date(estimatedFirstFrameTime).getTime()) /
          1000 -
        3 + // 3s hardcoded offset; works well for staging
        timeOffset // manual offset
      );
    },
    [timeOffset]
  );

  useEffect(() => {
    (async () => {
      if (!recordingId) return;
      try {
        const response = await getRecording({ recording_id: recordingId });
        if (response.status == 204) {
          // No Content
          setLoadingState("recordingInSession");
          return;
        }

        const { recording, transcript, signedUrl, host, participants } =
          response.data;

        if (!recording || !transcript) return;

        // Process translations and locales
        const translations = transcript.map((caption) => caption.translations);
        const locales = [
          ...new Set(
            translations.flatMap((translation) => Object.keys(translation))
          ),
        ];

        let timestamps: {
          timestamp: number;
          id: string;
        }[] = [];
        // Process timestamps
        if (recording.egress_started_at) {
          console.log(
            `recording.egress_started_at: ${recording.egress_started_at}`
          );
          timestamps = transcript.map((caption) => ({
            timestamp: computeCaptionTimestamp(
              caption,
              recording.egress_started_at ?? recording.created_at //fallback is for teams
            ),
            id: caption.id,
          }));
        }
        console.log(timestamps);
        // Batch state updates
        setRecording(recording);
        setTranscript(transcript);
        setMediaUrl(signedUrl);
        setAvailableLanguageLocales(locales);
        setRecordingLanguage(locales[0]);
        setCaptionTimestamps(timestamps);
        setHost(host);
        setParticipants(participants);
        setLoadingState("done");
      } catch (error) {
        console.error(error);
        if (isAxiosError(error)) {
          if (error.response?.status === HttpStatusCode.Unauthorized)
            setLoadingState("unauthorized");
          else if (error.response?.status === HttpStatusCode.NotFound)
            setLoadingState("notFound");
          else setLoadingState("error");
        } else setLoadingState("error");
      }
    })();
  }, [computeCaptionTimestamp, getRecording, recordingId]);

  // Long polling when summary generating
  useEffect(() => {
    let summaryPollingInterval: NodeJS.Timeout | undefined = undefined;

    if (
      !(recording?.summaries && "loading" in recording.summaries && recordingId)
    )
      return;

    summaryPollingInterval = setInterval(async () => {
      try {
        const response = await getRecording({ recording_id: recordingId });
        const updatedRecording = response.data.recording;

        if (
          updatedRecording &&
          (!updatedRecording.summaries ||
            !("loading" in updatedRecording.summaries))
        ) {
          // Summary is no longer loading, update the recording state
          setRecording((prevState) => ({
            ...prevState!,
            summaries: updatedRecording.summaries,
          }));
          clearInterval(summaryPollingInterval);
        }
      } catch (error) {
        console.error("Failed to poll for summary:", error);
        clearInterval(summaryPollingInterval);
        // Set error state in recording.summaries here
      }
    }, 3000); // Poll every 3 seconds

    return () => {
      if (summaryPollingInterval) {
        clearInterval(summaryPollingInterval);
      }
    };
  }, [recording?.summaries, recordingId, getRecording]);

  const handleGenerateSummaryClick = useCallback(async () => {
    if (!recording || !recordingId) return;
    try {
      setRecording((prevState) => ({
        ...prevState!,
        summaries: { loading: true },
      }));
      await generateSummary({ recordingId });
    } catch (error) {
      console.error(error);
    }
  }, [generateSummary, recording, recordingId]);
  const downloadTranscript = useCallback(async () => {
    if (!transcript || !transcript.length || !recordingLanguage || !recording)
      return;

    const startTime = new Date(transcript[0].first_created).getTime();

    // Create document content
    const doc = new Document({
      sections: [
        {
          children: [
            // Title
            new Paragraph({
              children: [
                new TextRun({
                  text: recording.name,
                  bold: true,
                  size: 36,
                }),
              ],
            }),
            // URL link
            new Paragraph({
              children: [
                new ExternalHyperlink({
                  children: [
                    new TextRun({
                      text: "VIEW RECORDING",
                      style: "Hyperlink",
                      bold: true,
                      break: 1,
                    }),
                  ],
                  link: location.href,
                }),
              ],
            }),
            // Transcript content
            ...transcript.map((caption) => {
              const seconds = Math.floor(
                (new Date(caption.first_created).getTime() - startTime) / 1000
              );
              const minutes = Math.floor(seconds / 60);
              const remainingSeconds = seconds % 60;
              const timestamp = `@${minutes}:${remainingSeconds
                .toString()
                .padStart(2, "0")}`;

              return new Paragraph({
                children: [
                  new TextRun({
                    text: `${timestamp} - ${caption.speaker_name}`,
                    bold: true,
                    break: 2,
                  }),
                  new TextRun({
                    text: caption.translations[recordingLanguage],
                    break: 1,
                  }),
                ],
              });
            }),
          ],
        },
      ],
    });

    // Generate blob
    const blob = await Packer.toBlob(doc);

    // Trigger download
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `transcript_${recording.created_at.slice(
      0,
      10
    )}_${recordingLanguage}.docx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [transcript, recordingLanguage, recording]);

  // Function that is call when audio/video element has a change in time. Fired every ~250ms
  // Essentially: input is currentTime of audio/video element. Action is to highlight the correct caption
  // Note: if function triggered from a caption click, early return & do not handle the caption highlight here
  const handleTimeUpdate = (event: React.SyntheticEvent) => {
    if (captionClicked) {
      setCaptionClicked(false);
      return;
    }
    const target = event.target as HTMLMediaElement;
    if (captionTimestamps && captionTimestamps.length) {
      const captionId = binarySearch(
        target.currentTime,
        0,
        captionTimestamps?.length - 1
      );
      setHighlightedCaption(captionId ? { id: captionId } : undefined);
    }
  };

  // target: timestamp of the caption we are looking for
  // start: start / left index
  // end: end / right index
  // returns: caption id of caption which was first_created JUST before target
  // Binary Search Algo adapted for ranges
  // Assumption: the timestamp array is perfectly matched with video
  const binarySearch = (
    target: number,
    start: number,
    end: number
  ): string | null => {
    if (start > end) {
      console.warn(`The timestamp ${target} was not found`);
      return null;
    }
    // middle index
    const middle = Math.floor((start + end) / 2);

    // search success condition is:
    // A: target is in between caption at index = middle and caption at index = (middle + 1)
    // B: target is after caption at index = middle if middle == end
    if (middle < end) {
      //condition A
      if (
        captionTimestamps[middle].timestamp <= target &&
        target < captionTimestamps[middle + 1].timestamp
      ) {
        return captionTimestamps[middle].id;
      }
    } else {
      // middle == end
      // condition B
      if (captionTimestamps[middle].timestamp <= target) {
        return captionTimestamps[middle].id;
      }
    }

    // Recursive steps
    if (captionTimestamps[middle].timestamp > target) {
      return binarySearch(target, start, middle - 1);
    }
    if (captionTimestamps[middle].timestamp < target) {
      return binarySearch(target, middle + 1, end);
    }

    console.warn(
      `The timestamp ${target} was not found. Start: ${start}, End: ${end}`
    );
    return null;
  };

  // Search functionality
  useEffect(() => {
    if (query && recordingLanguage) {
      setSearchedCaptions(
        transcript
          ?.filter((caption) => caption.translations[recordingLanguage])
          .filter((caption) =>
            caption.translations[recordingLanguage]
              .toLowerCase()
              .includes(query.toLowerCase())
          )
      );
    } else {
      setSearchedCaptions([]);
    }
  }, [query, recordingLanguage, transcript]);

  const [enableAutoScroll, setEnableAutoScroll] = useState(true);

  useEffect(() => {
    setEnableAutoScroll(!query.length);
  }, [query.length]);

  const prevHighlightedCaption = useRef<{ id: string } | undefined>(undefined);
  const recordingCaptionsRef = useRef<HTMLDivElement>();

  const scrollToCaption = (captionId: string) => {
    const container = recordingCaptionsRef.current;
    const caption = document.getElementById(captionId);
    if (container && caption) {
      // Calculate the scroll position
      const containerRect = container.getBoundingClientRect();
      const captionRect = caption.getBoundingClientRect();

      recordingCaptionsRef.current?.scrollTo({
        top: container.scrollTop + (captionRect.top - containerRect.top) - 100,
        behavior: "smooth",
      });
    }
  };
  useEffect(() => {
    if (
      recordingCaptionsRef.current &&
      enableAutoScroll &&
      highlightedCaption &&
      highlightedCaption.id !== prevHighlightedCaption.current?.id
    ) {
      scrollToCaption(highlightedCaption.id);
      prevHighlightedCaption.current = highlightedCaption;
    }
  }, [enableAutoScroll, highlightedCaption, recordingCaptionsRef]);

  const [shownQueryMatch, setShownQueryMatch] = useState(0);
  const showNextQueryMatch = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter" && searchedCaptions?.length) {
        // Get the first in the list (if exists)
        const id = searchedCaptions[shownQueryMatch];
        scrollToCaption(id.id);
        setShownQueryMatch((prevState) =>
          prevState < searchedCaptions.length - 1 ? prevState + 1 : 0
        );
      }
    },
    [searchedCaptions, shownQueryMatch]
  );

  const renderHighlight = useCallback(
    (text: string): string | ReactNode[] => {
      const escapeRegex = (text: string) => {
        return text.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
      };
      if (query.length) {
        const regex = new RegExp(`(${escapeRegex(query.toLowerCase())})`, "g");
        const split = text.toLowerCase().split(regex);
        if (split.length > 1) {
          let index = 0;
          return (
            split
              // Restore case
              .map((chunk) => {
                const start = index;
                const end = index + chunk.length;
                index += chunk.length;
                return text.substring(start, end);
              })
              // Render highlight
              .map((chunk, index) => {
                if (chunk.toLowerCase() === query.toLowerCase()) {
                  return <mark key={index}>{chunk}</mark>;
                } else {
                  return <span key={index}>{chunk}</span>;
                }
              })
          );
        }
      }
      return text;
    },
    [query]
  );

  const isMobile = useIsMobile();

  const renderTranscript = useMemo(() => {
    if (!recording) {
      return null;
    }
    return (
      <div className={classNames.RecordingTranscript}>
        <div className={classNames.RecordingPanelTitle}>
          <h2 className={classNames.TranscriptTitle}>Transcript</h2>
          <div className={classNames.TranscriptTitleActions}>
            <Button
              onClick={downloadTranscript}
              className={classNames.Download}
              color="white"
              borderColor="dark-gray"
              size="small"
            >
              <FiDownload size={20} />
            </Button>
            <Search
              query={query}
              setQuery={(value) => setQuery(value)}
              onKeyDown={showNextQueryMatch}
              className={classNames.Search}
            />

            {!!availableLanguageLocales?.length && (
              <Select
                selected={recordingLanguage}
                onChange={(value) =>
                  typeof value === "string" && setRecordingLanguage(value)
                }
                align="end"
                items={availableLanguageLocales.map((textLang) => ({
                  value: textLang,
                  label: textLanguageNames[textLang as TextLanguage],
                }))}
              />
            )}
          </div>
        </div>
        <SimpleBar
          scrollableNodeProps={{ ref: recordingCaptionsRef }}
          className={classNames.RecordingTranscriptCaptions}
        >
          {searchedCaptions &&
            recordingLanguage &&
            transcript
              .filter((caption) => caption.translations[recordingLanguage])
              .map((caption) => (
                <div
                  id={caption.id}
                  key={caption.id}
                  className={
                    classNames.RecordingCaption +
                    (highlightedCaption?.id === caption.id
                      ? ` ${classNames.HighlightedCaption}`
                      : "")
                  }
                  onClick={() => {
                    if (mediaPlayer.current) {
                      setCaptionClicked(true);
                      setHighlightedCaption({ id: caption.id });
                      mediaPlayer.current.currentTime = computeCaptionTimestamp(
                        caption,
                        recording.egress_started_at ?? recording.created_at //fallback is for teams
                      );
                    }
                  }}
                >
                  <div className={classNames.RecordingCaptionTitle}>
                    <span>{caption.speaker_name}</span>
                    <span className={classNames.RecordingCaptionTimestamp}>
                      {new Date(caption.first_created).toLocaleTimeString(
                        "en-US",
                        {
                          hour: "numeric",
                          minute: "numeric",
                        }
                      )}
                    </span>
                  </div>
                  <div>
                    {renderHighlight(caption.translations[recordingLanguage])}
                  </div>
                </div>
              ))}
        </SimpleBar>
      </div>
    );
  }, [
    availableLanguageLocales,
    computeCaptionTimestamp,
    downloadTranscript,
    highlightedCaption?.id,
    query,
    recording,
    recordingLanguage,
    renderHighlight,
    searchedCaptions,
    showNextQueryMatch,
    transcript,
  ]);

  const renderSummary = useMemo(() => {
    if (!recording) {
      return;
    }

    if (!user.auth && recording.summaries === null) {
      return;
    }

    return (
      <div className={classNames.Summary}>
        <h2 className={classNames.RecordingPanelTitle}>Summary</h2>

        {recording.summaries !== null && (
          <>
            <div>
              {"loading" in recording.summaries ? (
                <div className={classNames.Loader}>
                  <Loader inline={true} />
                </div>
              ) : "error" in recording.summaries ? (
                <p>Failed to generate summary. Please contact support.</p>
              ) : (
                <div className={classNames.SummaryContent}>
                  <ReactMarkdown>
                    {recording.summaries[recordingLanguage]}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          </>
        )}
        {user.auth && recording.summaries === null && recordingId && (
          <Button
            onClick={handleGenerateSummaryClick}
            style={{ marginTop: 16 }}
            size="medium"
          >
            Generate Summary
          </Button>
        )}
      </div>
    );
  }, [
    handleGenerateSummaryClick,
    recording,
    recordingId,
    recordingLanguage,
    user.auth,
  ]);

  if (loadingState === "loading") {
    return (
      <div className={classNames.Recording}>
        <Loader />
      </div>
    );
  } else if (loadingState === "recordingInSession") {
    return (
      <div className={classNames.Recording}>
        <Empty message="Recording not yet available. We'll alert you via email once it is ready" />
      </div>
    );
  } else if (loadingState === "unauthorized") {
    return (
      <div className={classNames.Recording}>
        <Error
          errorMessage="You are not authorized to view this recording."
          messageBody="Please sign in. If you are already signed in, please request the owner of the recording to share it with you"
        />
      </div>
    );
  } else if (loadingState === "error") {
    return (
      <div className={classNames.Recording}>
        <Error />
      </div>
    );
  } else if (loadingState === "notFound") {
    return (
      <div className={classNames.Recording}>
        <Error errorMessage="Sorry this recording is no longer available." />
      </div>
    );
  }
  return (
    <div className={classNames.Recording}>
      {transcript && recording && (
        <>
          <div className={classNames.RecordingHeader}>
            <Breadcrumbs
              items={[
                { label: "Home", url: "/" },
                { label: "Meetings", url: "/meetings" },
                { label: recording.name, url: location.href },
              ]}
            />
            <div className={classNames.RecordingTitle}>
              <h1>{recording.name}</h1>

              {user.auth?.id &&
                participants
                  .map((participant) => participant.id)
                  .includes(user.auth?.id) && (
                  <ShareRecording
                    recordingId={recording.id}
                    isPublic={recording.public}
                  />
                )}
            </div>
          </div>
          <div className={classNames.RecordingMainBody}>
            <div className={classNames.RecordingLeftSide}>
              {mediaUrl && (
                <>
                  <div className={classNames.RecordingPlayback}>
                    <h2 className={classNames.RecordingPanelTitle}>Playback</h2>

                    {mediaUrl && recording?.room_type === "meeting" && (
                      <video
                        src={mediaUrl}
                        autoPlay={false}
                        controls={true}
                        onTimeUpdate={handleTimeUpdate}
                        ref={(el) => {
                          mediaPlayer.current = el as HTMLVideoElement;
                        }}
                      />
                    )}
                    {mediaUrl && recording?.room_type === "event" && (
                      <audio
                        src={mediaUrl}
                        autoPlay={false}
                        controls={true}
                        onTimeUpdate={handleTimeUpdate}
                        ref={mediaPlayer}
                      />
                    )}
                  </div>
                </>
              )}

              {isMobile ? renderTranscript : renderSummary}
              <div className={classNames.RecordingAdditionalInfo}>
                <h2 className={classNames.RecordingPanelTitle}>
                  Additional Information
                </h2>
                <div>
                  <div>{`Date: ${new Date(
                    recording.created_at
                  ).toLocaleDateString()} ${new Date(
                    recording.created_at
                  ).toLocaleTimeString()}`}</div>
                </div>
                <br />
                {host && `Host: ${host}`}
                {recording.room_type === "meeting" && (
                  <>
                    <br />
                    <br />
                    Participants:{" "}
                    {participants
                      .map((participant) => participant.name)
                      .join(", ")}
                  </>
                )}
              </div>
            </div>

            {!isMobile ? renderTranscript : renderSummary}
          </div>
        </>
      )}
    </div>
  );
};

export default RecordingPage;
